import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Map, useControl } from 'react-map-gl/maplibre';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import { GeoJsonLayer, TextLayer } from '@deck.gl/layers';
import { Plus, Minus, Crosshair, Maximize2, Minimize2 } from 'lucide-react';
import 'maplibre-gl/dist/maplibre-gl.css';
import useTheme from '../../hooks/useTheme';
import { useBasemapTile } from '../../config/basemaps';
import { useCountry } from '../../context/CountryContext';
import { getBounds } from '../../utils/geoUtils';
import { geocodeDefaultsFor } from '../../utils/geocode';
import { cn } from '../../utils/cn';

/**
 * Mapa de calor de DENSIDAD de entregas (HeatmapLayer de deck.gl + MapLibre).
 *
 * El componente recibe las coordenadas CRUDAS del endpoint delivery-coords y la
 * agregación de densidad la hace la GPU por pantalla (KDE). No hay clustering
 * ni point-in-polygon en el hilo principal, así que pintar decenas de miles de
 * puntos no bloquea el navegador.
 *
 * Basemap: el MISMO sistema elegible por usuario que el resto de los mapas de
 * la app (src/config/basemaps.js — CARTO/Esri, variante clara/oscura, hot-swap
 * vía 'basemapchange'). Se materializa como estilo raster de MapLibre; nada de
 * estilos vectoriales externos (tiles.openfreemap.org no carga en todas las
 * redes y no respetaba la preferencia del usuario). La rampa de color del
 * heatmap conmuta con useTheme().
 *
 * Para que el calor no "tape" la geografía (los tiles raster traen los rótulos
 * horneados debajo del heatmap):
 *  - la opacidad del calor es regulable (slider, persistida en localStorage), y
 *  - una capa opcional de comunas (bordes + nombres de la geocerca de la región
 *    seleccionada, public/data/geofences) se dibuja ENCIMA del calor, así cada
 *    mancha se ubica de inmediato.
 *
 * Pantalla completa vía Fullscreen API nativa (top layer): un overlay CSS
 * `position: fixed` NO sirve aquí — .content-main-container retiene un
 * transform permanente (bt-fade-in-up con fill-mode both) y se vuelve el
 * containing block del fixed; además la bottom-nav mobile (z-index 1000)
 * pintaría encima. El top layer ignora ambos por diseño y el navegador maneja
 * Esc solo. En navegadores sin soporte (iPhone) el botón no se muestra.
 */

/**
 * Convierte un tile de basemaps.js (formato de URL de Leaflet) en un estilo
 * raster de MapLibre. MapLibre no entiende los placeholders {s}/{r}: los
 * subdominios se expanden a una URL por letra y el sufijo retina se omite.
 */
function rasterStyleFromTile(tile, isDark) {
  const subs = tile.subdomains ? Array.from(tile.subdomains) : [''];
  const tiles = subs.map((s) => tile.url.replace('{s}', s).replace('{r}', ''));
  return {
    version: 8,
    sources: {
      basemap: {
        type: 'raster',
        tiles,
        tileSize: 256,
        maxzoom: tile.maxZoom ?? 20,
        attribution: tile.attribution || '',
      },
    },
    layers: [
      // Fondo bajo los tiles: evita el flash blanco en dark mientras cargan.
      { id: 'background', type: 'background', paint: { 'background-color': isDark ? '#0a0a0a' : '#f2f2f0' } },
      { id: 'basemap', type: 'raster', source: 'basemap' },
    ],
  };
}

// Zoom del encuadre inicial por país. En Chile el centro por defecto (Santiago)
// concentra además la operación, así que un zoom urbano acierta; en Argentina el
// centro (Buenos Aires) no representa al operador promedio, así que se abre a
// escala país. En cualquier caso es solo el fallback: al cargar las geocercas se
// hace fitBounds al extent de la región seleccionada.
const INITIAL_ZOOM_BY_COUNTRY = { CL: 9, AR: 5 };
const DEFAULT_INITIAL_ZOOM = 5;

const FIT_OPTIONS = { padding: 48, maxZoom: 13, duration: 700 };

// Ancho mínimo en píxeles que debe ocupar una comuna para que su rótulo sea
// legible (por debajo, los nombres se pisan entre sí).
const LABEL_MIN_FEATURE_PX = 60;
// En Web Mercator el ancho del mundo es 512·2^zoom px sobre 360° de longitud, y
// los grados de longitud por píxel NO dependen de la latitud. De ahí sale el
// zoom al que una comuna de `w` grados de ancho ocupa LABEL_MIN_FEATURE_PX px.
const LABEL_ZOOM_NUMERATOR = (LABEL_MIN_FEATURE_PX * 360) / 512;
const LABEL_ZOOM_BOUNDS = [3, 12];
// Umbral usado mientras no hay geocercas cargadas (equivale al valor fijo que
// tenía la vista cuando era RM-only).
const DEFAULT_LABEL_MIN_ZOOM = 9;

// Rampas de 6 colores para HeatmapLayer (baja → alta densidad).
// Oscuro: púrpura → teal → verde → amarillo → magenta sobre basemap dark.
const HEAT_COLORS_DARK = [
  [70, 25, 120],
  [106, 60, 190],
  [40, 150, 160],
  [90, 190, 90],
  [245, 210, 70],
  [235, 65, 150],
];
// Claro: azul → cian → verde → amarillo → naranja → rojo sobre positron.
const HEAT_COLORS_LIGHT = [
  [37, 99, 235],
  [6, 182, 212],
  [34, 197, 94],
  [234, 179, 8],
  [249, 115, 22],
  [220, 38, 38],
];

// Preferencias del usuario para este mapa (opacidad del calor, capa comunas).
const PREFS_KEY = 'deliveryDensityMap.prefs';
function loadPrefs() {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY)) || {};
  } catch {
    return {};
  }
}

// Fullscreen API con fallback webkit (Safari < 16.4). En iPhone no existe
// fullscreen de elementos: el botón directamente no se renderiza.
const fullscreenEnabled = () =>
  typeof document !== 'undefined' &&
  (document.fullscreenEnabled || document.webkitFullscreenEnabled) === true;
const fullscreenElement = () =>
  document.fullscreenElement || document.webkitFullscreenElement || null;

/**
 * Integra deck.gl dentro del pipeline WebGL de MapLibre (interleaved): sin
 * canvas separado, y las capas se reinsertan solas en cada `styledata` (cambio
 * de tema/basemap en caliente incluido).
 */
function DeckGLOverlay(props) {
  const overlay = useControl(() => new MapboxOverlay({ interleaved: true, ...props }));
  overlay.setProps(props);
  return null;
}

const DeliveryDensityMap = ({ points = [], geojson = null }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { countryCode } = useCountry();
  // Basemap elegido por el usuario (mismo sistema que los mapas Leaflet de la
  // app); se re-renderiza en caliente al cambiarlo en Configuración o el tema.
  const tile = useBasemapTile(isDark);
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  // Vista inicial por país. `initialViewState` solo se lee al montar, así que
  // este memo no "mueve" el mapa después: es el punto de partida antes del
  // primer fitBounds.
  const initialViewState = useMemo(() => {
    const { center } = geocodeDefaultsFor(countryCode);
    return {
      longitude: center.lng,
      latitude: center.lat,
      zoom: INITIAL_ZOOM_BY_COUNTRY[String(countryCode || '').toUpperCase()] ?? DEFAULT_INITIAL_ZOOM,
      pitch: 0,
      bearing: 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);
  const initialViewRef = useRef(initialViewState);
  initialViewRef.current = initialViewState;

  // Zoom actual (actualizado al terminar cada gesto): gatea los rótulos de
  // comuna sin re-render por frame durante el pan/zoom.
  const [zoom, setZoom] = useState(initialViewState.zoom);
  const [heatOpacity, setHeatOpacity] = useState(() => {
    const v = loadPrefs().opacity;
    return typeof v === 'number' && v >= 0.2 && v <= 1 ? v : 0.7;
  });
  const [showComunas, setShowComunas] = useState(() => loadPrefs().showComunas !== false);
  // Extent ya encuadrado: evita re-fitBounds cuando cambia el tema o el resize,
  // pero re-encuadra cuando llega un dataset distinto (otro rango de fechas).
  const fittedKeyRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({ opacity: heatOpacity, showComunas }));
    } catch {}
  }, [heatOpacity, showComunas]);

  // Sincroniza el estado con el fullscreen real del navegador (cubre Esc,
  // que el propio navegador maneja al salir del top layer).
  useEffect(() => {
    const sync = () => setFullscreen(fullscreenElement() === containerRef.current);
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (fullscreenElement() === el) {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    } else {
      const request = el.requestFullscreen || el.webkitRequestFullscreen;
      try {
        const p = request?.call(el);
        if (p?.catch) p.catch(() => {});
      } catch {}
    }
  }, []);

  const mapStyle = useMemo(() => rasterStyleFromTile(tile, isDark), [tile, isDark]);

  // Valida finitud y rango WGS84: una sola fila corrupta (NaN, 999, string…)
  // contaminaría el extent del fitBounds y podría tumbar la vista; el backend
  // solo garantiza not-null.
  const data = useMemo(
    () =>
      points.filter(
        (p) =>
          p &&
          Number.isFinite(p.latitude) && Number.isFinite(p.longitude) &&
          Math.abs(p.latitude) <= 90 && Math.abs(p.longitude) <= 180
      ),
    [points]
  );

  // Extent de la REGIÓN seleccionada (la de las geocercas cargadas). Es el
  // encuadre por defecto: el desglose por comuna se acota a esa región, así que
  // encuadrar al extent de TODAS las entregas dejaba el mapa a escala país con
  // la región analizada como una mancha diminuta.
  // `getBounds` devuelve orden Leaflet [[minLat,minLng],[maxLat,maxLng]]; aquí
  // se pasa al orden de MapLibre [[minLng,minLat],[maxLng,maxLat]].
  const regionBounds = useMemo(() => {
    const features = geojson?.features;
    if (!features?.length) return null;
    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
    for (const f of features) {
      const b = getBounds(f.geometry);
      if (!b) continue;
      if (b[0][1] < minLng) minLng = b[0][1];
      if (b[1][1] > maxLng) maxLng = b[1][1];
      if (b[0][0] < minLat) minLat = b[0][0];
      if (b[1][0] > maxLat) maxLat = b[1][0];
    }
    return Number.isFinite(minLng) ? [[minLng, minLat], [maxLng, maxLat]] : null;
  }, [geojson]);

  // Extent de los puntos: fallback cuando todavía no hay geocercas (o fallaron).
  const dataBounds = useMemo(() => {
    if (!data.length) return null;
    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
    for (const { longitude, latitude } of data) {
      if (longitude < minLng) minLng = longitude;
      if (longitude > maxLng) maxLng = longitude;
      if (latitude < minLat) minLat = latitude;
      if (latitude > maxLat) maxLat = latitude;
    }
    return [[minLng, minLat], [maxLng, maxLat]];
  }, [data]);

  const bounds = regionBounds ?? dataBounds;

  const fitToData = useCallback((animate = true) => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    if (bounds) {
      map.fitBounds(bounds, { ...FIT_OPTIONS, duration: animate ? FIT_OPTIONS.duration : 0 });
    } else {
      const view = initialViewRef.current;
      map.flyTo({
        center: [view.longitude, view.latitude],
        zoom: view.zoom,
        duration: animate ? FIT_OPTIONS.duration : 0,
      });
    }
  }, [bounds]);

  // Encuadre inicial y por cambio de dataset: espera a que el mapa cargue
  // (mapReady) y solo re-encuadra si el extent es realmente otro.
  useEffect(() => {
    if (!mapReady || !bounds) return;
    const key = bounds.flat().join(',');
    if (fittedKeyRef.current === key) return;
    fittedKeyRef.current = key;
    fitToData(true);
  }, [mapReady, bounds, fitToData]);

  const handleLoad = useCallback(() => {
    setMapReady(true);
  }, []);

  const colorRange = isDark ? HEAT_COLORS_DARK : HEAT_COLORS_LIGHT;

  const heatmapLayer = useMemo(() => {
    if (!data.length) return null;
    return new HeatmapLayer({
      id: 'delivery-density-heatmap',
      data,
      getPosition: (d) => [d.longitude, d.latitude],
      getWeight: 1,
      radiusPixels: 40,
      intensity: 1,
      threshold: 0.05,
      colorRange,
      opacity: heatOpacity,
      aggregation: 'SUM',
      pickable: false,
    });
  }, [data, colorRange, heatOpacity]);

  // Bordes de comuna SOBRE el calor: ubican cada mancha sin depender de los
  // rótulos del basemap (que quedan debajo del heatmap).
  const comunaOutlineLayer = useMemo(() => {
    if (!showComunas || !geojson) return null;
    return new GeoJsonLayer({
      id: 'comuna-outlines',
      data: geojson,
      stroked: true,
      filled: false,
      getLineColor: isDark ? [255, 255, 255, 90] : [30, 41, 59, 110],
      lineWidthMinPixels: 1,
      pickable: false,
    });
  }, [showComunas, geojson, isDark]);

  // Rótulos en el punto interior precalculado por el pipeline de geocercas
  // (properties.labelX/labelY). Reemplaza el centroide calculado en runtime, que
  // además caía fuera del polígono en comunas cóncavas o archipiélagos.
  const labelData = useMemo(() => {
    if (!geojson?.features) return [];
    return geojson.features
      .map((f) => {
        const { name, labelX, labelY } = f.properties || {};
        return name && Number.isFinite(labelX) && Number.isFinite(labelY)
          ? { name, position: [labelX, labelY] }
          : null;
      })
      .filter(Boolean);
  }, [geojson]);

  // Umbral de rótulos derivado del tamaño real de las comunas de la región
  // cargada. Con el umbral fijo de 9 que tenía la vista RM-only, las regiones
  // grandes (Magallanes, Aysén, provincias patagónicas) no rotulaban nunca:
  // a ese zoom no entra ni una comuna completa en pantalla.
  const labelMinZoom = useMemo(() => {
    const features = geojson?.features;
    if (!features?.length) return DEFAULT_LABEL_MIN_ZOOM;
    const widths = [];
    for (const f of features) {
      const b = getBounds(f.geometry);
      if (b) widths.push(b[1][1] - b[0][1]); // maxLng - minLng
    }
    if (!widths.length) return DEFAULT_LABEL_MIN_ZOOM;
    widths.sort((a, b) => a - b);
    const median = widths[Math.floor(widths.length / 2)];
    if (!(median > 0)) return DEFAULT_LABEL_MIN_ZOOM;
    const z = Math.round(Math.log2(LABEL_ZOOM_NUMERATOR / median));
    return Math.min(LABEL_ZOOM_BOUNDS[1], Math.max(LABEL_ZOOM_BOUNDS[0], z));
  }, [geojson]);

  const comunaLabelLayer = useMemo(() => {
    if (!showComunas || zoom < labelMinZoom || !labelData.length) return null;
    return new TextLayer({
      id: 'comuna-labels',
      data: labelData,
      getPosition: (d) => d.position,
      getText: (d) => d.name,
      getSize: 12,
      sizeUnits: 'pixels',
      characterSet: 'auto',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSettings: { sdf: true },
      getColor: isDark ? [255, 255, 255, 235] : [15, 23, 42, 235],
      // outlineWidth NO es en px de pantalla: TextLayer lo divide por
      // fontSettings.radius (12); con 2 el halo quedaba sub-pixel (~0.26 px)
      // e ilegible sobre manchas calientes. Con 8 ≈ 1.7 px reales a size 12.
      outlineWidth: 8,
      outlineColor: isDark ? [0, 0, 0, 200] : [255, 255, 255, 220],
      pickable: false,
    });
  }, [showComunas, zoom, labelMinZoom, labelData, isDark]);

  const layers = useMemo(
    () => [heatmapLayer, comunaOutlineLayer, comunaLabelLayer].filter(Boolean),
    [heatmapLayer, comunaOutlineLayer, comunaLabelLayer]
  );

  const zoomBy = useCallback((delta) => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    map.easeTo({ zoom: map.getZoom() + delta, duration: 250 });
  }, []);

  const legendGradient = `linear-gradient(to right, ${colorRange
    .map(([r, g, b]) => `rgb(${r},${g},${b})`)
    .join(', ')})`;

  const ctrlBtn =
    'h-8 w-8 inline-flex items-center justify-center rounded-lg border border-border ' +
    'bg-bg/90 backdrop-blur-sm text-fg-muted shadow-sm transition-colors ' +
    'hover:text-brand-700 hover:border-brand-300';

  const pill =
    'flex items-center gap-2 rounded-lg border border-border bg-bg/90 backdrop-blur-sm px-2.5 py-1.5 shadow-sm';

  return (
    <div className="relative h-full w-full">
      {/* Con la Fullscreen API el elemento se pinta en el top layer pero
          conserva su lugar en el DOM: el hueco del card muestra este texto. */}
      {fullscreen && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-fg-muted">
          {t('Mapa en pantalla completa')}
        </div>
      )}

      <div ref={containerRef} className="absolute inset-0 bg-bg">
        <Map
          ref={mapRef}
          initialViewState={initialViewState}
          mapStyle={mapStyle}
          style={{ width: '100%', height: '100%' }}
          onLoad={handleLoad}
          onMoveEnd={(e) => setZoom(e.viewState.zoom)}
        >
          <DeckGLOverlay layers={layers} />
        </Map>

        <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5">
          <button type="button" className={ctrlBtn} onClick={() => zoomBy(1)} aria-label={t('Acercar')} title={t('Acercar')}>
            <Plus className="w-4 h-4" />
          </button>
          <button type="button" className={ctrlBtn} onClick={() => zoomBy(-1)} aria-label={t('Alejar')} title={t('Alejar')}>
            <Minus className="w-4 h-4" />
          </button>
          <button type="button" className={ctrlBtn} onClick={() => fitToData(true)} aria-label={t('Encuadrar entregas')} title={t('Encuadrar entregas')}>
            <Crosshair className="w-4 h-4" />
          </button>
          {fullscreenEnabled() && (
            <button
              type="button"
              className={ctrlBtn}
              onClick={toggleFullscreen}
              aria-label={fullscreen ? t('Salir de pantalla completa') : t('Pantalla completa')}
              title={fullscreen ? t('Salir de pantalla completa') : t('Pantalla completa')}
            >
              {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          )}
        </div>

        <div className="absolute bottom-3 left-3 z-10 flex flex-col gap-1.5">
          <div className={cn(pill, 'pointer-events-none')}>
            <span className="text-[10px] leading-none text-fg-muted">{t('Baja')}</span>
            <span className="h-1.5 w-24 rounded-full" style={{ background: legendGradient }} />
            <span className="text-[10px] leading-none text-fg-muted">{t('Alta')}</span>
          </div>
          <div className={pill}>
            <label className="flex items-center gap-1.5 text-[10px] leading-none text-fg-muted cursor-pointer select-none">
              {t('Opacidad')}
              <input
                type="range"
                min={20}
                max={100}
                step={5}
                value={Math.round(heatOpacity * 100)}
                onChange={(e) => setHeatOpacity(Number(e.target.value) / 100)}
                className="w-20 accent-brand-500 cursor-pointer"
                aria-label={t('Opacidad')}
              />
            </label>
            {geojson && (
              <label className="flex items-center gap-1.5 text-[10px] leading-none text-fg-muted cursor-pointer select-none border-l border-border pl-2">
                <input
                  type="checkbox"
                  checked={showComunas}
                  onChange={(e) => setShowComunas(e.target.checked)}
                  className="accent-brand-500 cursor-pointer"
                />
                {t('Comunas')}
              </label>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryDensityMap;
